// PositionLimitsTab — Running position cards, value-date ladder,
// limit monitor, override request form.

import React, { useState } from 'react';
import { Scale, AlertTriangle, TrendingUp, TrendingDown, Plus } from 'lucide-react';

const formatCurrency = (val) => {
  if (val == null) return '—';
  const abs = Math.abs(val);
  if (abs >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val.toLocaleString();
};

const PositionLimitsTab = ({ bookContext, dealerLimits, trades = [] }) => {
  const [overrideForm, setOverrideForm] = useState(null);

  const positions = bookContext?.positions || {};
  const nopPct = bookContext?.nopUtilization != null ? (bookContext.nopUtilization * 100).toFixed(1) : '—';

  // Build value-date ladder from trades
  const valueDateMap = {};
  trades.forEach(t => {
    const vd = t.settlement?.valueDate;
    if (!vd) return;
    if (!valueDateMap[vd]) valueDateMap[vd] = { buy: 0, sell: 0 };
    if (t.side === 'buy') valueDateMap[vd].buy += t.amount;
    else valueDateMap[vd].sell += t.amount;
  });
  const valueDates = Object.entries(valueDateMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(0, 15);

  return (
    <div className="space-y-6">
      {/* NOP Summary */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-5 h-5 text-zinc-400" />
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Net Open Position (NOP)</h3>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">NOP Current</p>
            <p className="text-2xl font-bold text-black">{formatCurrency(bookContext?.nopCurrent)}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">NOP Limit</p>
            <p className="text-2xl font-bold text-black">{formatCurrency(bookContext?.nopLimit)}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">Utilization</p>
            <p className={`text-2xl font-bold ${(bookContext?.nopUtilization || 0) > 0.9 ? 'text-red-700' : 'text-black'}`}>
              {nopPct}%
            </p>
          </div>
        </div>
        <div className="w-full h-3 bg-zinc-100 rounded-none">
          <div
            className="h-3 rounded-none transition-all"
            style={{
              width: `${Math.min(Number(nopPct) || 0, 100)}%`,
              backgroundColor: (bookContext?.nopUtilization || 0) > 0.9 ? '#991b1b' : (bookContext?.nopUtilization || 0) > 0.7 ? '#3f3f46' : '#52525b',
            }}
          />
        </div>
      </div>

      {/* Position Cards */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Running Position by Currency</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(positions).sort().map(([ccy, pos]) => {
            const net = (pos.long || 0) - (pos.short || 0);
            const utilization = pos.limit ? Math.abs(net) / pos.limit : 0;
            const utilPct = (utilization * 100).toFixed(1);
            const barColor = utilization > 0.8 ? '#991b1b' : utilization > 0.6 ? '#3f3f46' : '#52525b';

            return (
              <div key={ccy} className="border border-zinc-200 rounded-none p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-sm font-bold text-black">{ccy}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${utilization > 0.8 ? 'text-red-700' : 'text-zinc-400'}`}>
                    {utilPct}% utilized
                  </span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> Long (Bought)
                    </span>
                    <span className="font-mono">{formatCurrency(pos.long)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" /> Short (Sold)
                    </span>
                    <span className="font-mono">{formatCurrency(pos.short)}</span>
                  </div>
                  <div className="flex justify-between border-t border-zinc-100 pt-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Net</span>
                    <span className={`font-mono font-bold ${net >= 0 ? 'text-zinc-700' : 'text-zinc-500'}`}>
                      {net >= 0 ? '+' : ''}{formatCurrency(net)} {net < 0 ? '(short)' : '(long)'}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="w-full h-2 bg-zinc-100 rounded-none">
                      <div className="h-2 rounded-none transition-all" style={{ width: `${Math.min(utilization * 100, 100)}%`, backgroundColor: barColor }} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="font-mono text-[10px] text-zinc-400">Limit: {formatCurrency(pos.limit)}</span>
                    {utilization > 0.8 && (
                      <AlertTriangle className="w-3 h-3 text-red-600" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Value-Date Ladder */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Value-Date Ladder</h3>
        {valueDates.length === 0 ? (
          <p className="text-xs text-zinc-400">No upcoming value dates</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Value Date</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Buy</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Sell</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Net</th>
                </tr>
              </thead>
              <tbody>
                {valueDates.map(([date, { buy, sell }]) => {
                  const net = buy - sell;
                  return (
                    <tr key={date} className="border-b border-zinc-100">
                      <td className="py-2 px-3 font-mono text-xs">{date}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-zinc-600">{formatCurrency(buy)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-zinc-600">{formatCurrency(sell)}</td>
                      <td className={`py-2 px-3 text-right font-mono text-xs font-medium ${net >= 0 ? 'text-zinc-700' : 'text-zinc-500'}`}>
                        {net >= 0 ? '+' : ''}{formatCurrency(net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dealer Daily Limits */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Dealer Limits</h3>
          <button
            onClick={() => setOverrideForm(overrideForm ? null : 'daily')}
            className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-xs font-mono hover:bg-zinc-50"
          >
            <Plus className="w-3 h-3" />
            Request Override
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">Daily Volume</p>
            <p className="font-mono text-sm">
              {formatCurrency(dealerLimits?.dailyVolume?.used)} / {formatCurrency(dealerLimits?.dailyVolume?.max)}
            </p>
            <div className="w-full h-2 bg-zinc-100 rounded-none mt-1">
              <div
                className="h-2 rounded-none transition-all bg-zinc-600"
                style={{ width: `${Math.min(((dealerLimits?.dailyVolume?.used || 0) / (dealerLimits?.dailyVolume?.max || 1)) * 100, 100)}%` }}
              />
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">Single Deal Max</p>
            <p className="font-mono text-sm">{formatCurrency(dealerLimits?.singleDeal?.max)}</p>
          </div>
        </div>

        {overrideForm && (
          <div className="mt-4 border border-zinc-200 rounded-none p-3 bg-zinc-50">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Override Request</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Limit type..."
                className="px-2 py-1 border border-zinc-200 rounded-none text-xs font-mono focus:outline-none"
              />
              <input
                type="number"
                placeholder="New limit..."
                className="px-2 py-1 border border-zinc-200 rounded-none text-xs font-mono focus:outline-none"
              />
            </div>
            <textarea
              placeholder="Justification..."
              className="w-full mt-2 px-2 py-1 border border-zinc-200 rounded-none text-xs font-mono focus:outline-none resize-none"
              rows={2}
            />
            <div className="flex gap-2 mt-2">
              <button className="px-3 py-1 bg-zinc-700 text-white text-xs font-mono uppercase rounded-none hover:bg-zinc-800">
                Submit Request
              </button>
              <button
                onClick={() => setOverrideForm(null)}
                className="px-3 py-1 border border-zinc-200 text-xs font-mono uppercase rounded-none hover:bg-zinc-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionLimitsTab;
